/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

// Component imports
import { Button, Col, Form, Input, List, Modal, Row, Select, Skeleton, Space, Typography } from 'antd';
import BotAvatar from '../UI/BotAvatar';

// misc imports
import is from '../../utils/is';
import { useFetchBotsQuery } from '../../store/bots/botsApi.js';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';

// Constant declarations
const { Option } = Select;

const BulkActionsCloneModal = ({
  cloneEntityCount=0,
  isModalOpen=false,
  onModalClose=()=>{},
  loader=false,
  dataToBeCloned=[],
  onShowMore=()=>{},
  onSubmit=()=>{},
  configOverrides= {
    modalTitle: 'Clone Flows',
    tenantIdDescription: 'Select the agent you want to add the cloned flows to.',
    newNameDescription: 'The cloned flow name',
    submitBtnTitle: 'Clone Flows'
  }
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { orgId, tenantId } = useParams();
  const { primaryBotLanguage } = useBotLanguage();

  const { data: bots, isFetching: isFetchingBots } = useFetchBotsQuery({ orgId }, { skip: !orgId });

  const [showConflictMessage, setShowConflictMessage] = useState(false);

  useEffect(() => {
    setShowConflictMessage(false);
    // Reset data on activity change
    return () => form.resetFields();
  }, [dataToBeCloned]);

  const checkForLanguageConflicts = (botId) => {
    const selectedBot = bots.items?.find(bot => bot.id === botId);
    setShowConflictMessage(selectedBot?.settings?.languages?.primary !== primaryBotLanguage);
  };

  return (
    <Modal
      title={t(configOverrides.modalTitle)}
      open={isModalOpen}
      onCancel={onModalClose}
      className={cloneEntityCount > 10 ? 'scrollable-modal' : null}
      footer={null}
      style={{ maxWidth: 600 }}
      width={'95%'}
      centered
      destroyOnClose
    >
      <Row gutter={24}>
        <Col xs={24}>
          <Form
            wrapperCol={{ span: 24 }}
            name="recordsCloneForm"
            layout="vertical"
            onFinish={onSubmit}
            initialValues={{
              tenantId: tenantId
            }}
            form={form}
            requiredMark={false}
          >
            <Row gutter={16} className="mb-3">
              <Col
                xs={24}
                className={classNames({ 'mb-3': showConflictMessage })}
              >
                <Form.Item
                  name="tenantId"
                  label={
                    <Row>
                      <Col span={24}>
                        <Typography.Text>
                          {t('Agent')}
                        </Typography.Text>
                      </Col>
                      <Col span={24} className="mb-1">
                        <Typography.Text type="secondary">
                          {t(configOverrides.tenantIdDescription)}
                        </Typography.Text>
                      </Col>
                    </Row>
                  }
                  rules={[{
                    required: true,
                    message: t('Please choose an agent.')
                  }]}
                  validateStatus={showConflictMessage ? 'warning' : undefined}
                  help={showConflictMessage
                    ? t('This agent has a different primary language than the current agent, which may lead to inconsistencies or issues in certain parts of the content.')
                    : undefined
                  }
                >
                  <Select
                    placeholder={t('Choose an agent')}
                    size="large"
                    className="fs-base mb-1"
                    loading={isFetchingBots}
                    showSearch
                    onChange={checkForLanguageConflicts}
                    filterOption={(input, option) =>
                      bots && is.array(bots.items) && bots.items.find(bot => bot.id === option.value).displayName.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {
                      // The dataset is all bots, minus the bots that were added already
                      bots && is.array(bots.items) && [...bots.items].sort((a, b) => a.displayName.localeCompare(b.displayName)).map(bot => (
                        <Option key={bot.id} value={bot.id}>
                          {
                            isFetchingBots ? (
                              <Skeleton active title={{ width: '80%' }} paragraph={false} className="mt-3 border-radius-base" />
                            ) : (
                              <BotAvatar bot={bot} avatarSize={24} />
                            )
                          }
                        </Option>
                      ))
                    }
                  </Select>
                </Form.Item>
              </Col>

              {
                dataToBeCloned?.length === 1
                  ? (
                    <Col xs={24}>
                      <Form.Item
                        name="newName"
                        initialValue={`${dataToBeCloned[0].name?.[primaryBotLanguage]} (Copy)`}
                        label={
                          <Row>
                            <Col span={24}>
                              <Typography.Text>
                                {t('Name')}
                              </Typography.Text>
                            </Col>
                            <Col span={24} className="mb-1">
                              <Typography.Text type="secondary">
                                {t(configOverrides.newNameDescription)}.
                              </Typography.Text>
                            </Col>
                          </Row>
                        }
                      >
                        <Input
                          size="large"
                          autoFocus={true}
                          onFocus={(e) => e.target.select()}
                        />
                      </Form.Item>
                    </Col>
                  )
                  : (
                    <Col xs={24}>
                      <List
                        className="mt-4"
                        dataSource={dataToBeCloned.slice(0, cloneEntityCount)}
                        renderItem={(item) => {
                          return <List.Item key={item.id || uuidv4()}>
                            <Space size={20}>
                              {`${item.name?.[primaryBotLanguage]} (Copy)`}
                            </Space>
                          </List.Item>;
                        }}
                      />
                      {
                        dataToBeCloned.length > cloneEntityCount ? (
                          <Button
                            className="mt-3"
                            type="link"
                            onClick={() => onShowMore(cloneEntityCount + 5)}
                          >
                            {t('Show more')} ...
                          </Button>
                        ) : null
                      }
                    </Col>
                  )
              }

            </Row>

            <Row justify="end">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loader}
                disabled={loader || isFetchingBots}
              >
                {t(configOverrides.submitBtnTitle)}
              </Button>
            </Row>
          </Form>
        </Col>
      </Row>
    </Modal>
  );
};

BulkActionsCloneModal.propTypes = {
  cloneEntityCount: PropTypes.number,
  isModalOpen: PropTypes.bool,
  onModalClose: PropTypes.func,
  loader: PropTypes.bool,
  dataToBeCloned: PropTypes.array,
  onShowMore: PropTypes.func,
  onSubmit: PropTypes.func,
  configOverrides: PropTypes.shape({
    modalTitle: PropTypes.string,
    tenantIdDescription: PropTypes.string,
    newNameDescription: PropTypes.string,
    submitBtnTitle: PropTypes.string
  })
};

export default BulkActionsCloneModal;
